import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { type Slot, dayKey, groupSlotsByDay } from '../lib/slots';

const VISIBLE_DAYS = 7;
const WINDOW_DAYS = 14;

/**
 * The day-strip + time-grid slot picker, shared by the per-staff BookPage and
 * the "anyone available" flow so they stay visually identical. Owns its own
 * day-pager + selected-day state; the parent owns the chosen slot (so it can
 * render the booking form alongside).
 */
export default function SlotPicker({
  slots,
  tz,
  picked,
  onPick,
}: {
  slots: Slot[];
  tz: string;
  picked: Slot | null;
  onPick: (s: Slot) => void;
}) {
  const [dayOffset, setDayOffset] = useState(0);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  // Drop slots that already started (a long-open page can tick past them).
  const nowMs = Date.now();
  const futureSlots = slots.filter((s) => new Date(s.startUtc).getTime() > nowMs);
  const grouped = groupSlotsByDay(futureSlots, tz);

  const today = new Date();
  const visibleDays = Array.from({ length: VISIBLE_DAYS }, (_, i) => {
    const d = new Date(today.getTime() + (dayOffset + i) * 24 * 60 * 60 * 1000);
    return { date: d, key: dayKey(d, tz) };
  });

  // Auto-select the first day that has slots once data lands.
  if (selectedDay === null && grouped.size > 0) {
    const first = Array.from(grouped.keys()).sort()[0];
    if (first) queueMicrotask(() => setSelectedDay((s) => s ?? first));
  }

  if (grouped.size === 0) {
    return <p className="muted">No open times in the next 2 weeks.</p>;
  }

  const canPrev = dayOffset > 0;
  const canNext = dayOffset + VISIBLE_DAYS < WINDOW_DAYS;
  const daySlots = selectedDay ? grouped.get(selectedDay) ?? [] : [];

  return (
    <div className="card">
      <div className="day-strip-header">
        <button
          className="icon-btn"
          onClick={() => setDayOffset((o) => Math.max(0, o - VISIBLE_DAYS))}
          disabled={!canPrev}
          aria-label="Previous week"
        >
          <ChevronLeft size={16} />
        </button>
        <div className="day-strip">
          {visibleDays.map(({ date, key }) => {
            const count = grouped.get(key)?.length ?? 0;
            const disabled = count === 0;
            const isSelected = selectedDay === key;
            return (
              <button
                key={key}
                className={`day-pill ${isSelected ? 'is-selected' : ''}`}
                disabled={disabled}
                onClick={() => setSelectedDay(key)}
              >
                <span className="day-pill-dow">
                  {date.toLocaleDateString('en-US', { timeZone: tz, weekday: 'short' })}
                </span>
                <span className="day-pill-date">
                  {date.toLocaleDateString('en-US', { timeZone: tz, month: 'short', day: 'numeric' })}
                </span>
                <span className="day-pill-count">
                  {disabled ? '—' : `${count} slot${count === 1 ? '' : 's'}`}
                </span>
              </button>
            );
          })}
        </div>
        <button
          className="icon-btn"
          onClick={() => setDayOffset((o) => Math.min(WINDOW_DAYS - VISIBLE_DAYS, o + VISIBLE_DAYS))}
          disabled={!canNext}
          aria-label="Next week"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      {selectedDay && (
        <>
          <h2 style={{ margin: '1.25rem 0 0.75rem' }}>
            {new Date(`${selectedDay}T12:00:00`).toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
            })}
          </h2>
          {daySlots.length === 0 ? (
            <p className="muted">No open times on this day. Pick another.</p>
          ) : (
            <div className="slot-grid">
              {daySlots.map((s) => (
                <button
                  key={s.startUtc}
                  onClick={() => onPick(s)}
                  className={picked?.startUtc === s.startUtc ? 'primary slot' : 'slot'}
                >
                  {new Date(s.startUtc).toLocaleTimeString('en-US', {
                    timeZone: tz,
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                  })}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
